// frontend/src/pages/GenericClientsPage.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import useApi from '../hooks/useApi';
import { 
    PlusCircle, Search, Edit, Trash2, X, User, ChevronDown, ChevronUp, UserPlus, 
    Info, History, FileText, TrendingUp, Camera, DollarSign, Settings,
    Loader2, ArrowLeft, ArrowRight, List, MapPin, Mail, Phone, AlertTriangle, CheckCircle, Table, PieChart as PieChartIcon,
    Key, Lock, Briefcase // Importando ícones para os novos campos e abas
} from 'lucide-react';
// IMPORTANTE: Instale recharts no frontend: npm install recharts
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';

// --- NOVO: HOOK useDebounce ---
const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);
        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);
    return debouncedValue;
};


// --- Sub-componente: ImageUpload ---
const ImageUpload = ({ currentImagePath, placeholderIcon, onFileSelect }) => {
    const [preview, setPreview] = useState(null);
    const fileInputRef = useRef(null);
    const [hasError, setHasError] = useState(false);

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setPreview(URL.createObjectURL(file));
            onFileSelect(file);
            setHasError(false);
        }
    };

    useEffect(() => {
        setPreview(null);
        setHasError(false);
    }, [currentImagePath]);

    const imageUrl = preview || (currentImagePath ? `/api/uploads/generic_images/${currentImagePath}?t=${new Date().getTime()}` : null);

    const handleImageError = () => {
        setHasError(true);
    };

    return (
        <div className="relative w-24 h-24">
            {imageUrl && !hasError ? (
                <img
                    src={imageUrl}
                    alt="Perfil"
                    className="w-24 h-24 rounded-full object-cover border-2 border-gray-200"
                    onError={handleImageError}
                />
            ) : (
                <div className="w-24 h-24 rounded-full bg-gray-100 flex items-center justify-center border-2 border-gray-200">
                    {placeholderIcon}
                </div>
            )}
            
            <input
                type="file"
                accept="image/png, image/jpeg"
                ref={fileInputRef}
                onChange={handleFileChange}
                className="hidden"
            />
            <button
                type="button"
                onClick={() => fileInputRef.current.click()}
                className="absolute bottom-0 right-0 p-1.5 bg-blue-600 text-white rounded-full hover:bg-blue-700 focus:outline-none ring-2 ring-white"
                title="Alterar foto"
            >
                <Camera size={14} />
            </button>
        </div>
    );
};


// --- Sub-componente: Modal/Formulário de Cliente (Versão 'General') ---
const ClientFormModal = ({ clientToEdit, onFinish, onDataChange }) => {
    const isEditing = !!clientToEdit;
    const [activeTab, setActiveTab] = useState('info'); 
    const { apiFetch } = useApi(); 
    
    // Estados para Tabela de Preços
    const [availableServices, setAvailableServices] = useState([]);
    const [clientPrices, setClientPrices] = useState({}); // { service_id: price }

    // Estados para o Dashboard
    const [dashboardData, setDashboardData] = useState(null);
    const [loadingDashboard, setLoadingDashboard] = useState(false);

    // Carregar serviços e preços ao montar ou mudar de aba
    useEffect(() => {
        if (activeTab === 'prices') {
            // Carregar Serviços Disponíveis
            apiFetch('/service-table').then(data => {
                setAvailableServices(data || []);
            }).catch(err => console.error("Erro ao carregar serviços:", err));

            // Se estiver editando, carregar preços do cliente
            if (isEditing) {
                apiFetch(`/general-clients/${clientToEdit.id}/prices`).then(data => {
                    const pricesMap = {};
                    if (data && Array.isArray(data)) {
                        data.forEach(p => pricesMap[p.service_id] = p.price);
                    }
                    setClientPrices(pricesMap);
                }).catch(err => console.error("Erro ao carregar preços:", err));
            }
        }

        // Carregar dados do Dashboard
        if (activeTab === 'dashboard' && isEditing) {
            setLoadingDashboard(true);
            apiFetch(`/general-clients/${clientToEdit.id}/dashboard-stats`)
                .then(data => setDashboardData(data))
                .catch(err => console.error("Erro dashboard:", err))
                .finally(() => setLoadingDashboard(false));
        }
    }, [activeTab, isEditing, clientToEdit, apiFetch]);

    // Handler para mudança de preço
    const handlePriceChange = (serviceId, value) => {
        const numValue = parseFloat(value);
        setClientPrices(prev => ({
            ...prev,
            [serviceId]: isNaN(numValue) ? '' : numValue
        }));
    };

    // --- Funções de Formatação de Documento ---
    const formatCPF = (value) => {
        if (!value) return '';
        const onlyNums = value.replace(/[^\d]/g, '');
        if (onlyNums.length <= 11) {
            return onlyNums
                .replace(/(\d{3})(\d)/, '$1.$2')
                .replace(/(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
                .replace(/(\d{3})\.(\d{3})\.(\d{3})(\d{1,2})/, '$1.$2.$3-$4');
        }
        return value.substring(0, 14);
    };
    
    const formatCNPJ = (value) => {
        if (!value) return '';
        const onlyNums = value.replace(/[^\d]/g, '');
        if (onlyNums.length <= 14) {
            return onlyNums
                .replace(/(\d{2})(\d)/, '$1.$2')
                .replace(/(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
                .replace(/(\d{2})\.(\d{3})\.(\d{3})(\d)/, '$1.$2.$3/$4')
                .replace(/(\d{2})\.(\d{3})\.(\d{3})\/(\d{4})(\d{1,2})/, '$1.$2.$3/$4-$5');
        }
        return value.substring(0, 18);
    };

    // --- ESTADOS ---
    const getInitialClientState = () => {
        const defaultState = {
            name: '',
            email: '',
            phone: '',
            birth_date: null,
            gender: 'masculino',
            document_type: 'CPF', // Padrão CPF
            document_id: '',
            inscricao_estadual: '',
            profile_picture_path: null,
            address_street: '',
            address_number: '',
            address_complement: '',
            address_city: '',
            address_state: '',
            address_zip: '',
            billing_contact_name: '',
            billing_email: '',
            billing_phone: '',
            billing_notes: '', 
            config_link: '',
            classification: 'Cliente',
            // CAMPOS DE ACESSO E REPASSE
            access_password: '',
            access_keyword: '',
            allow_client_access: 0,
            is_repasse: false, // NOVO: Flag de repasse
            addresses: [{
                address_street: '',
                address_number: '',
                address_complement: '',
                address_city: '',
                address_state: '',
                address_zip: ''
            }],
            // NOVO: Array inicial de sub-clientes
            subclients: [] 
        };

        if (!isEditing) {
            return defaultState;
        }
        
        const docType = clientToEdit.document_type || 'CPF';
        let docId = clientToEdit.document_id || '';
        
        if (docType === 'CPF') docId = formatCPF(docId);
        if (docType === 'CNPJ') docId = formatCNPJ(docId);
        
        // Lógica de retrocompatibilidade dos endereços
        let initialAddresses = clientToEdit.addresses || [];
        if (initialAddresses.length === 0) {
            initialAddresses = [{
                address_street: clientToEdit.address_street || '',
                address_number: clientToEdit.address_number || '',
                address_complement: clientToEdit.address_complement || '',
                address_city: clientToEdit.address_city || '',
                address_state: clientToEdit.address_state || '',
                address_zip: clientToEdit.address_zip || ''
            }];
        }

        // NOVO: Retrocompatibilidade da flag de repasse e sub-clientes
        const isRepasse = clientToEdit.is_repasse === 1 || clientToEdit.is_repasse === true;
        const initialSubclients = clientToEdit.subclients || [];

        return { 
            ...defaultState, 
            ...clientToEdit, 
            document_type: docType, 
            document_id: docId, 
            addresses: initialAddresses,
            is_repasse: isRepasse,
            subclients: initialSubclients
        };
    };

    const [client, setClient] = useState(getInitialClientState);
    const [clientPhotoFile, setClientPhotoFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // --- Handlers de Mudança ---
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        // NOVO: Tratamento para checkbox (is_repasse)
        const finalValue = type === 'checkbox' ? checked : value;
        setClient(prev => ({ ...prev, [name]: finalValue }));
    };

    // --- Handlers de Múltiplos Endereços ---
    const handleAddressChange = (index, field, value) => {
        setClient(prev => {
            const newAddresses = [...prev.addresses];
            newAddresses[index][field] = value;
            return { ...prev, addresses: newAddresses };
        });
    };

    const handleAddAddress = () => {
        setClient(prev => ({
            ...prev,
            addresses: [...prev.addresses, { 
                address_street: '', address_number: '', address_complement: '', 
                address_city: '', address_state: '', address_zip: '' 
            }]
        }));
    };

    const handleRemoveAddress = (index) => {
        setClient(prev => {
            const newAddresses = [...prev.addresses];
            newAddresses.splice(index, 1);
            return { ...prev, addresses: newAddresses };
        });
    };

    // --- NOVO: Handlers de Múltiplos Sub-Clientes (Repasse) ---
    const handleSubclientChange = (index, field, value) => {
        setClient(prev => {
            const newSubclients = [...prev.subclients];
            
            // Aplica formatação de CNPJ se for o campo correspondente
            if (field === 'cnpj') {
                let formatted = formatCNPJ(value);
                if (formatted.length > 18) formatted = formatted.substring(0, 18);
                newSubclients[index][field] = formatted;
            } else {
                newSubclients[index][field] = value;
            }
            
            return { ...prev, subclients: newSubclients };
        });
    };

    const handleAddSubclient = () => {
        setClient(prev => ({
            ...prev,
            subclients: [...prev.subclients, { 
                razao_social: '', cnpj: '', telefone: '', endereco: '' 
            }]
        }));
    };

    const handleRemoveSubclient = (index) => {
        setClient(prev => {
            const newSubclients = [...prev.subclients];
            newSubclients.splice(index, 1);
            return { ...prev, subclients: newSubclients };
        });
    };
    // ------------------------------------------------------------

    const isCNPJ = client.document_type === 'CNPJ';

    const handleDocumentTypeChange = (e) => {
        const newType = e.target.value;
        setClient(prev => ({ 
            ...prev, 
            document_type: newType, 
            document_id: '',
            birth_date: null,
            gender: null,
            inscricao_estadual: ''
        }));
    };

    const handleDocumentIdChange = (e) => {
        let value = e.target.value;
        if (client.document_type === 'CPF') {
            value = formatCPF(value);
            if (value.length > 14) value = value.substring(0, 14);
        } else if (client.document_type === 'CNPJ') {
            value = formatCNPJ(value);
            if (value.length > 18) value = value.substring(0, 18);
        } else { // ID Global
            if (value.length > 30) value = value.substring(0, 30);
        }
        setClient(prev => ({ ...prev, document_id: value }));
    };
    
    // --- LÓGICA DE SUBMISSÃO ---
    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        const servicePricesArray = Object.keys(clientPrices).map(serviceId => ({
            service_id: parseInt(serviceId),
            price: clientPrices[serviceId]
        })).filter(item => item.price !== '' && item.price !== null);

        // Força o primeiro endereço na raiz do payload para compatibilidade
        const payload = {
            ...client,
            address_street: client.addresses[0]?.address_street || '',
            address_number: client.addresses[0]?.address_number || '',
            address_complement: client.addresses[0]?.address_complement || '',
            address_city: client.addresses[0]?.address_city || '',
            address_state: client.addresses[0]?.address_state || '',
            address_zip: client.addresses[0]?.address_zip || '',
            servicePrices: servicePricesArray,
            // Garante que a API receba a lista filtrada de sub-clientes (removendo linhas totalmente vazias)
            subclients: client.is_repasse ? client.subclients.filter(sub => sub.razao_social && sub.razao_social.trim() !== '') : []
        };

        try {
            let savedClient;
            if (isEditing) {
                await apiFetch(`/general-clients/${client.id}`, { method: 'PUT', body: payload });
                savedClient = client;
            } else {
                const newClientData = await apiFetch('/general-clients', { method: 'POST', body: payload });
                savedClient = { ...client, id: newClientData.id };
            }
            const clientId = savedClient.id;

            if (clientPhotoFile) {
                const clientFormData = new FormData();
                clientFormData.append('clientPhoto', clientPhotoFile);
                await apiFetch(`/general-clients/${clientId}/upload-photo`, {
                    method: 'POST',
                    body: clientFormData,
                });
            }

            setLoading(false);
            onDataChange();
            onFinish();

        } catch (err) {
            setError(`Erro ao salvar: ${err.message}`);
            setLoading(false);
        }
    };
    
    // MODIFICADO: A aba de repasse só aparece se is_repasse for true
    const tabs = [
        { id: 'dashboard', label: 'Dashboard', icon: TrendingUp },
        { id: 'info', label: 'Info. Pessoais', icon: User },
        { id: 'billing', label: 'Dados de Faturamento', icon: DollarSign },
        { id: 'prices', label: 'Tabela de Preços', icon: Table }, 
        { id: 'repasse', label: 'Sub-clientes (Repasse)', icon: Briefcase, hide: !client.is_repasse },
        { id: 'config', label: 'Configurações', icon: Settings, hide: client.allow_client_access !== 1 },
    ].filter(tab => !tab.hide);

    // --- RENDERIZAÇÃO GRÁFICA DO DASHBOARD ---
    const renderDashboard = () => {
        if (!isEditing) return <p className="text-center p-8 text-gray-500">Salve o cliente primeiro para ver o dashboard.</p>;
        if (loadingDashboard) return <div className="flex justify-center p-8"><Loader2 className="animate-spin text-blue-600" /></div>;
        if (!dashboardData) return <p className="text-center p-8 text-gray-500">Nenhum dado disponível.</p>;

        const { revenue, volume } = dashboardData;
        const COLORS = ['#3b82f6', '#e5e7eb']; // Azul (Cliente), Cinza (Outros)

        const formatCurrency = (val) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val);

        const renderPieCard = (title, dataObj, isMoney) => {
            const chartData = [
                { name: 'Este Cliente', value: dataObj.myValue },
                { name: 'Outros Clientes', value: dataObj.othersValue }
            ];
            
            // Evita gráfico vazio se tudo for zero
            if (dataObj.myValue === 0 && dataObj.othersValue === 0) {
                return (
                    <div className="bg-white p-6 rounded-lg shadow border border-gray-100 flex flex-col items-center justify-center h-64">
                        <h3 className="text-lg font-bold text-gray-700 mb-2">{title}</h3>
                        <p className="text-gray-400">Sem dados suficientes.</p>
                    </div>
                );
            }

            return (
                <div className="bg-white p-6 rounded-lg shadow border border-gray-100 flex flex-col h-80">
                    <h3 className="text-lg font-bold text-gray-700 mb-4 flex items-center gap-2">
                        {isMoney ? <DollarSign size={20} /> : <FileText size={20} />}
                        {title}
                    </h3>
                    
                    <div className="flex-1 flex flex-col md:flex-row items-center">
                        {/* Gráfico */}
                        <div className="h-48 w-48 relative">
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={chartData}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={40}
                                        outerRadius={70}
                                        paddingAngle={5}
                                        dataKey="value"
                                    >
                                        {chartData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip formatter={(val) => isMoney ? formatCurrency(val) : val} />
                                </PieChart>
                            </ResponsiveContainer>
                            {/* Texto Central */}
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                <span className="text-xs font-bold text-gray-400">Rank</span>
                            </div>
                        </div>

                        {/* Legenda e Rank */}
                        <div className="flex-1 ml-0 md:ml-6 mt-4 md:mt-0 text-center md:text-left space-y-2">
                            <div className="text-3xl font-bold text-blue-600">
                                {dataObj.rank}º <span className="text-sm font-normal text-gray-500">lugar</span>
                            </div>
                            <p className="text-sm text-gray-600">
                                de {dataObj.totalClients} clientes ativos
                            </p>
                            <div className="mt-4 pt-4 border-t w-full">
                                <p className="text-sm text-gray-500">Total Cliente: <span className="font-semibold text-gray-800">{isMoney ? formatCurrency(dataObj.myValue) : dataObj.myValue}</span></p>
                            </div>
                        </div>
                    </div>
                </div>
            );
        };

        return (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {renderPieCard('Rank Financeiro (Faturamento)', revenue, true)}
                {renderPieCard('Rank Operacional (Qtd. Serviços)', volume, false)}
                
                <div className="md:col-span-2 bg-blue-50 p-4 rounded-lg border border-blue-100 flex items-start gap-3">
                    <Info className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
                    <p className="text-sm text-blue-800">
                        <strong>Nota:</strong> O ranking considera apenas Ordens de Serviço com status <em>Faturada</em>, <em>Concluída</em> ou <em>Paga</em>.
                    </p>
                </div>
            </div>
        );
    };
    
    return (
        // CORREÇÃO: Removido onClick={onFinish} do backdrop
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div 
                className="bg-white rounded-lg shadow-xl w-full max-w-4xl h-[90vh] flex flex-col" 
                onClick={e => e.stopPropagation()}
            >
                <div className="flex items-center justify-between p-4 border-b">
                    <h2 className="text-xl font-bold">{isEditing ? `Editar: ${client.name}` : 'Novo Cliente / Fornecedor'}</h2>
                    <button onClick={onFinish} className="p-2 rounded-full hover:bg-gray-200"><X /></button>
                </div>
                
                <div className="flex border-b overflow-x-auto">
                    {tabs.map(tab => (
                        <button 
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            disabled={!isEditing && tab.id === 'dashboard'} 
                            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === tab.id ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'} disabled:text-gray-300 disabled:cursor-not-allowed`}
                        >
                            <tab.icon size={16} /> {tab.label}
                        </button>
                    ))}
                </div>

                <form onSubmit={handleSubmit} className="flex-1 flex flex-col overflow-hidden">
                    <div className="flex-1 overflow-y-auto p-6 space-y-6">

                        {activeTab === 'dashboard' && renderDashboard()}
                        
                        {activeTab === 'info' && (
                            <div className="flex flex-col md:flex-row gap-6">
                                <div className="flex-shrink-0">
                                    <label className="text-sm font-medium mb-1 block">Foto</label>
                                    <ImageUpload 
                                        currentImagePath={client.profile_picture_path}
                                        placeholderIcon={<User size={40} className="text-gray-400" />}
                                        onFileSelect={setClientPhotoFile}
                                    />
                                </div>
                                
                                <div className="flex-grow space-y-4">
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div>
                                            <label className="text-sm font-medium">Nº</label>
                                            <input type="text" value={client.client_number || 'Auto'} disabled className="w-full p-2 mt-1 border rounded bg-gray-100" />
                                        </div>
                                        <div className="md:col-span-2">
                                            <label className="text-sm font-medium">Nome / Razão Social</label>
                                            <input type="text" name="name" value={client.name || ''} onChange={handleChange} required className="w-full p-2 mt-1 border rounded" />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1">
                                        <div>
                                            <label className="text-sm font-medium">Tipo de Entidade</label>
                                            <select
                                                name="classification"
                                                value={client.classification || 'Cliente'}
                                                onChange={handleChange}
                                                className="w-full p-2 mt-1 border rounded"
                                            >
                                                <option value="Cliente">Cliente</option>
                                                <option value="Fornecedor">Fornecedor</option>
                                                <option value="Ambos">Ambos</option>
                                            </select>
                                        </div>
                                    </div>

                                    {/* --- NOVO: Checkbox de Cliente de Repasse --- */}
                                    {(client.classification === 'Cliente' || client.classification === 'Ambos') && (
                                        <div className="flex items-center gap-2 mt-2 p-3 bg-blue-50 border border-blue-100 rounded-md">
                                            <input 
                                                type="checkbox" 
                                                id="is_repasse" 
                                                name="is_repasse" 
                                                checked={client.is_repasse || false} 
                                                onChange={handleChange}
                                                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                            />
                                            <label htmlFor="is_repasse" className="text-sm font-semibold text-blue-900 cursor-pointer">
                                                Este é um Cliente de Repasse (Terceirizado)
                                            </label>
                                        </div>
                                    )}
                                    {/* ------------------------------------------- */}
                                    
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div>
                                            <label className="text-sm font-medium">Tipo de Documento</label>
                                            <select 
                                                name="document_type" 
                                                value={client.document_type || 'CPF'} 
                                                onChange={handleDocumentTypeChange} 
                                                className="w-full p-2 mt-1 border rounded"
                                            >
                                                <option value="CPF">CPF</option>
                                                <option value="CNPJ">CNPJ</option>
                                                <option value="ID Global">ID Global (Passaporte, etc.)</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="text-sm font-medium">Documento ID</label>
                                            <input 
                                                type="text" 
                                                name="document_id" 
                                                value={client.document_id || ''} 
                                                onChange={handleDocumentIdChange} 
                                                className="w-full p-2 mt-1 border rounded" 
                                                placeholder={client.document_type === 'CPF' ? '000.000.000-00' : (client.document_type === 'CNPJ' ? '00.000.000/0000-00' : 'ID Alfanumérico')}
                                                maxLength={client.document_type === 'CNPJ' ? 18 : (client.document_type === 'CPF' ? 14 : 30)}
                                            />
                                        </div>
                                        <div>
                                            <label className="text-sm font-medium">Insc. Estadual</label>
                                            <input 
                                                type="text" 
                                                name="inscricao_estadual" 
                                                value={client.inscricao_estadual || ''} 
                                                onChange={handleChange} 
                                                disabled={!isCNPJ}
                                                className="w-full p-2 mt-1 border rounded disabled:bg-gray-100 disabled:cursor-not-allowed" 
                                                title={isCNPJ ? "Insira a Inscrição Estadual" : "Apenas para CNPJ"}
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="text-sm font-medium">Data de Nascimento</label>
                                            <input 
                                                type="date" 
                                                name="birth_date" 
                                                value={client.birth_date || ''} 
                                                onChange={handleChange} 
                                                disabled={isCNPJ}
                                                className="w-full p-2 mt-1 border rounded disabled:bg-gray-100 disabled:cursor-not-allowed" 
                                                title={isCNPJ ? "Apenas para CPF" : ""}
                                            />
                                        </div>
                                        <div>
                                            <label className="text-sm font-medium">Gênero</label>
                                            <select 
                                                name="gender" 
                                                value={client.gender || 'masculino'} 
                                                onChange={handleChange} 
                                                disabled={isCNPJ}
                                                className="w-full p-2 mt-1 border rounded disabled:bg-gray-100 disabled:cursor-not-allowed"
                                                title={isCNPJ ? "Apenas para CPF" : ""}
                                            >
                                                <option value="masculino">Masculino</option>
                                                <option value="feminino">Feminino</option>
                                                <option value="outro">Outro</option>
                                            </select>
                                        </div>
                                    </div>
                                    
                                    {/* BLOCO DINÂMICO DE ENDEREÇOS */}
                                    <div className="pt-4 border-t space-y-4">
                                        <div className="flex justify-between items-center">
                                            <h3 className="text-lg font-semibold">Endereços</h3>
                                        </div>

                                        {client.addresses.map((addr, index) => (
                                            <div key={index} className="p-4 border rounded-md bg-gray-50 relative space-y-4">
                                                <div className="flex justify-between items-center mb-2 border-b pb-2">
                                                    <h4 className="text-sm font-bold text-gray-700">
                                                        Endereço {index + 1} {index === 0 ? '(Principal)' : ''}
                                                    </h4>
                                                    {index > 0 && (
                                                        <button 
                                                            type="button" 
                                                            onClick={() => handleRemoveAddress(index)} 
                                                            className="text-red-500 hover:text-red-700 text-xs font-semibold flex items-center gap-1"
                                                        >
                                                            <Trash2 size={14} /> Remover
                                                        </button>
                                                    )}
                                                </div>
                                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
                                                    <div className="md:col-span-2">
                                                        <label className="text-sm font-medium">Rua/Avenida</label>
                                                        <input type="text" name="address_street" value={addr.address_street || ''} onChange={(e) => handleAddressChange(index, 'address_street', e.target.value)} className="w-full p-2 mt-1 border rounded bg-white" />
                                                    </div>
                                                    <div>
                                                        <label className="text-sm font-medium">Nº</label>
                                                        <input type="text" name="address_number" value={addr.address_number || ''} onChange={(e) => handleAddressChange(index, 'address_number', e.target.value)} className="w-full p-2 mt-1 border rounded bg-white" />
                                                    </div>
                                                    <div className="md:col-span-3">
                                                        <label className="text-sm font-medium">Complemento</label>
                                                        <input type="text" name="address_complement" value={addr.address_complement || ''} onChange={(e) => handleAddressChange(index, 'address_complement', e.target.value)} className="w-full p-2 mt-1 border rounded bg-white" />
                                                    </div>
                                                    <div>
                                                        <label className="text-sm font-medium">Cidade</label>
                                                        <input type="text" name="address_city" value={addr.address_city || ''} onChange={(e) => handleAddressChange(index, 'address_city', e.target.value)} className="w-full p-2 mt-1 border rounded bg-white" />
                                                    </div>
                                                    <div>
                                                        <label className="text-sm font-medium">Estado</label>
                                                        <input type="text" name="address_state" value={addr.address_state || ''} onChange={(e) => handleAddressChange(index, 'address_state', e.target.value)} className="w-full p-2 mt-1 border rounded bg-white" />
                                                    </div>
                                                    <div>
                                                        <label className="text-sm font-medium">CEP</label>
                                                        <input type="text" name="address_zip" value={addr.address_zip || ''} onChange={(e) => handleAddressChange(index, 'address_zip', e.target.value)} className="w-full p-2 mt-1 border rounded bg-white" />
                                                    </div>
                                                </div>
                                            </div>
                                        ))}

                                        <button 
                                            type="button" 
                                            onClick={handleAddAddress} 
                                            className="mt-2 px-3 py-1.5 bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100 text-sm font-semibold flex items-center gap-1"
                                        >
                                            <PlusCircle size={16} /> Adicionar Novo Endereço
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* --- NOVA ABA: SUB-CLIENTES (REPASSE) --- */}
                        {activeTab === 'repasse' && client.is_repasse && (
                            <div className="space-y-6">
                                <div>
                                    <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                                        <Briefcase size={20} className="text-blue-600" />
                                        Sub-clientes do Repasse
                                    </h3>
                                    <p className="text-sm text-gray-600 mt-1">
                                        Cadastre aqui as empresas ou filiais onde os serviços serão de fato executados. Os laudos técnicos sairão em nome destas empresas, enquanto o faturamento continuará em nome do cliente de Repasse ({client.name || 'Cliente Principal'}).
                                    </p>
                                </div>

                                <div className="space-y-4">
                                    {client.subclients && client.subclients.length === 0 && (
                                        <div className="p-8 text-center bg-gray-50 border border-dashed border-gray-300 rounded-lg">
                                            <p className="text-gray-500 font-medium">Nenhum sub-cliente cadastrado para este repasse.</p>
                                        </div>
                                    )}

                                    {client.subclients && client.subclients.map((sub, index) => (
                                        <div key={index} className="p-4 border border-blue-100 rounded-md bg-blue-50/30 relative space-y-4 shadow-sm">
                                            <div className="flex justify-between items-center mb-2 border-b border-blue-100 pb-2">
                                                <h4 className="text-sm font-bold text-blue-900">
                                                    Sub-cliente {index + 1}
                                                </h4>
                                                <button 
                                                    type="button" 
                                                    onClick={() => handleRemoveSubclient(index)} 
                                                    className="text-red-500 hover:text-red-700 text-xs font-semibold flex items-center gap-1"
                                                >
                                                    <Trash2 size={14} /> Remover
                                                </button>
                                            </div>
                                            
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                                                <div className="md:col-span-2">
                                                    <label className="text-sm font-bold text-gray-700">Razão Social / Nome <span className="text-red-500">*</span></label>
                                                    <input 
                                                        type="text" 
                                                        value={sub.razao_social || ''} 
                                                        onChange={(e) => handleSubclientChange(index, 'razao_social', e.target.value)} 
                                                        className="w-full p-2 mt-1 border rounded bg-white focus:ring-2 focus:ring-blue-500" 
                                                        placeholder="Ex: Empresa X Filial Centro"
                                                        required
                                                    />
                                                </div>
                                                <div>
                                                    <label className="text-sm font-bold text-gray-700">CNPJ / Documento</label>
                                                    <input 
                                                        type="text" 
                                                        value={sub.cnpj || ''} 
                                                        onChange={(e) => handleSubclientChange(index, 'cnpj', e.target.value)} 
                                                        className="w-full p-2 mt-1 border rounded bg-white focus:ring-2 focus:ring-blue-500" 
                                                        placeholder="00.000.000/0000-00"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="text-sm font-bold text-gray-700">Telefone (Opcional)</label>
                                                    <input 
                                                        type="text" 
                                                        value={sub.telefone || ''} 
                                                        onChange={(e) => handleSubclientChange(index, 'telefone', e.target.value)} 
                                                        className="w-full p-2 mt-1 border rounded bg-white focus:ring-2 focus:ring-blue-500" 
                                                    />
                                                </div>
                                                <div className="md:col-span-2">
                                                    <label className="text-sm font-bold text-gray-700">Endereço Completo</label>
                                                    <input 
                                                        type="text" 
                                                        value={sub.endereco || ''} 
                                                        onChange={(e) => handleSubclientChange(index, 'endereco', e.target.value)} 
                                                        className="w-full p-2 mt-1 border rounded bg-white focus:ring-2 focus:ring-blue-500" 
                                                        placeholder="Ex: Rua A, 123 - Centro, Cidade - UF"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    ))}

                                    <button 
                                        type="button" 
                                        onClick={handleAddSubclient} 
                                        className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-semibold flex items-center gap-2 shadow-sm"
                                    >
                                        <PlusCircle size={16} /> Adicionar Empresa de Repasse
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Aba Dados de Faturamento */}
                        {activeTab === 'billing' && (
                            <div className="space-y-4 max-w-lg">
                                <div>
                                    <label className="text-sm font-medium">Responsável Financeiro</label>
                                    <input type="text" name="billing_contact_name" value={client.billing_contact_name || ''} onChange={handleChange} className="w-full p-2 mt-1 border rounded" />
                                </div>
                                <div>
                                    <label className="text-sm font-medium">Email para envio de NF</label>
                                    <input type="email" name="billing_email" value={client.billing_email || ''} onChange={handleChange} className="w-full p-2 mt-1 border rounded" />
                                </div>
                                <div>
                                    <label className="text-sm font-medium">Telefone Financeiro</label>
                                    <input type="tel" name="billing_phone" value={client.billing_phone || ''} onChange={handleChange} className="w-full p-2 mt-1 border rounded" />
                                </div>
                                <div>
                                    <label className="text-sm font-medium">Observações</label>
                                    <textarea 
                                        name="billing_notes" 
                                        value={client.billing_notes || ''} 
                                        onChange={handleChange} 
                                        className="w-full p-2 mt-1 border rounded h-32 resize-none"
                                        placeholder="Informações adicionais sobre o faturamento..."
                                    />
                                </div>
                            </div>
                        )}
                        
                        {/* Aba Tabela de Preços */}
                        {activeTab === 'prices' && (
                            <div className="space-y-4">
                                <h3 className="text-lg font-semibold text-gray-800">Tabela de Preços Personalizada</h3>
                                <p className="text-sm text-gray-600 mb-4">Defina valores específicos para este cliente. Deixe em branco para usar o valor padrão (se houver).</p>
                                
                                <div className="overflow-hidden border rounded-lg">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Serviço</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-40">Validade (Meses)</th>
                                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-48">Valor (R$)</th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {availableServices.map(service => (
                                                <tr key={service.id}>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                        {service.description}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                        {service.validity_months}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                                                        <input 
                                                            type="number" 
                                                            step="0.01"
                                                            min="0"
                                                            placeholder="0,00"
                                                            value={clientPrices[service.id] || ''}
                                                            onChange={(e) => handlePriceChange(service.id, e.target.value)}
                                                            className="w-full p-2 border rounded-md text-right focus:ring-blue-500 focus:border-blue-500"
                                                        />
                                                    </td>
                                                </tr>
                                            ))}
                                            {availableServices.length === 0 && (
                                                <tr>
                                                    <td colSpan="3" className="px-6 py-4 text-center text-sm text-gray-500">
                                                        Nenhum serviço cadastrado na Tabela de Serviços.
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {/* Aba Configurações (SÓ RENDERIZA SE allow_client_access === 1) */}
                        {activeTab === 'config' && client.allow_client_access === 1 && (
                             <div className="space-y-6 max-w-lg">
                                {/* Link de Configuração Existente */}
                                <div>
                                    <label className="text-sm font-medium">Link de Configuração</label>
                                    <input type="url" name="config_link" value={client.config_link || ''} onChange={handleChange} className="w-full p-2 mt-1 border rounded" placeholder="https://..." />
                                </div>

                                {/* SEÇÃO DE ACESSO AO SISTEMA */}
                                <div className="p-4 bg-blue-50 border border-blue-100 rounded-lg space-y-4">
                                    <h3 className="text-md font-bold text-blue-900 flex items-center gap-2">
                                        <Lock size={18} />
                                        Acesso ao Portal do Cliente
                                    </h3>
                                    
                                    <div>
                                        <label className="text-sm font-medium text-blue-800 flex items-center gap-2">
                                            <Key size={16} />
                                            Senha de Acesso ao Sistema
                                        </label>
                                        <input 
                                            type="text" 
                                            name="access_password" 
                                            value={client.access_password || ''} 
                                            onChange={handleChange} 
                                            className="w-full p-2 mt-1 border border-blue-200 rounded focus:ring-2 focus:ring-blue-500" 
                                            placeholder="Digite a senha de acesso..." 
                                        />
                                        <p className="text-xs text-blue-600 mt-1">
                                            Senha utilizada para login no portal exclusivo do cliente.
                                        </p>
                                    </div>

                                    <div>
                                        <label className="text-sm font-medium text-blue-800">
                                            Palavra-chave
                                        </label>
                                        <input 
                                            type="text" 
                                            name="access_keyword" 
                                            value={client.access_keyword || ''} 
                                            onChange={handleChange} 
                                            className="w-full p-2 mt-1 border border-blue-200 rounded focus:ring-2 focus:ring-blue-500" 
                                            placeholder="Ex: Frase de segurança..." 
                                        />
                                        <p className="text-xs text-blue-600 mt-1">
                                            Utilizada para recuperação de conta ou verificação adicional.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    
                    {/* Rodapé com botões e erro */}
                    <div className="flex items-center justify-between p-4 bg-gray-50 border-t">
                        <div className="flex-1 mr-4">
                            {error && <div className="p-2 text-sm text-red-700 bg-red-100 rounded">{error}</div>}
                        </div>
                        
                        <div className="flex-shrink-0">
                            <button type="button" onClick={onFinish} className="px-4 py-2 mr-2 font-semibold bg-gray-200 rounded-md">Cancelar</button>
                            <button type="submit" disabled={loading} className="px-4 py-2 font-semibold text-white bg-blue-600 rounded-md disabled:bg-gray-400">
                                {loading ? 'A guardar...' : 'Guardar'}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};


const ConfirmDeleteModal = ({ onClose, onConfirm, loading, error, message }) => {
    return (
        // CORREÇÃO: Removido onClick={onClose}
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md" onClick={e => e.stopPropagation()}>
                <div className="flex items-start justify-between p-4 border-b">
                    <div className="flex items-center gap-3">
                        <AlertTriangle className="text-red-500" size={24} />
                        <h2 className="text-xl font-bold text-gray-800">Confirmar Exclusão</h2>
                    </div>
                    <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-200"><X size={20} /></button>
                </div>
                <div className="p-6">
                    {error ? (
                        <p className="p-3 text-sm text-red-700 bg-red-100 rounded-md">{error}</p>
                    ) : (
                        <p className="text-gray-600">{message}</p>
                    )}
                </div>
                <div className="flex justify-end gap-2 p-4 bg-gray-50 border-t">
                    <button type="button" onClick={onClose} disabled={loading} className="px-4 py-2 font-semibold bg-gray-200 rounded-md">Cancelar</button>
                    {!error && (
                        <button 
                            onClick={onConfirm}
                            disabled={loading} 
                            className="flex items-center justify-center px-4 py-2 font-semibold text-white bg-red-600 rounded-md disabled:bg-red-300"
                        >
                            {loading ? <Loader2 className="animate-spin" /> : 'Sim, Excluir'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};


// --- Componente Principal da Página ---
const GenericClientsPage = ({ currentUser, onDataChange }) => {
    const { apiFetch, permissions } = useApi(); 
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    
    // Estado da pesquisa separado (valor limpo vs. valor com máscara)
    const [searchQuery, setSearchQuery] = useState(''); // Valor limpo para a API
    const [searchMaskedValue, setSearchMaskedValue] = useState(''); // Valor com máscara para a UI

    // NOVO: Estado para o filtro de classificação
    const [filterClassification, setFilterClassification] = useState('Todos');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false); 
    const [clientToDelete, setClientToDelete] = useState(null); 
    const [editingClient, setEditingClient] = useState(null);
    const [expandedClientId, setExpandedClientId] = useState(null);
    const [imgError, setImgError] = useState({});

    // --- MODIFICAÇÃO: ESTADOS DE PAGINAÇÃO ---
    const [totalPages, setTotalPages] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);
    
    // --- MODIFICAÇÃO: Debounce da pesquisa (valor limpo) ---
    const debouncedSearchQuery = useDebounce(searchQuery, 300); // Linha 497

    // VERIFICAÇÃO DE PERMISSÕES GRANULARES
    const canCreate = permissions?.clients_create || currentUser?.is_super_user;
    const canEdit = permissions?.clients_edit || currentUser?.is_super_user;
    const canDelete = permissions?.clients_delete || currentUser?.is_super_user;

    // Funções de formatação (para pesquisa e exibição)
    const formatCPF = (value) => {
        if (!value) return '';
        const onlyNums = value.replace(/[^\d]/g, '');
        return onlyNums
            .replace(/(\d{3})(\d)/, '$1.$2')
            .replace(/(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
            .replace(/(\d{3})\.(\d{3})\.(\d{3})(\d{1,2})/, '$1.$2.$3-$4')
            .substring(0, 14);
    };
    
    const formatCNPJ = (value) => {
        if (!value) return '';
        const onlyNums = value.replace(/[^\d]/g, '');
        return onlyNums
            .replace(/(\d{2})(\d)/, '$1.$2')
            .replace(/(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
            .replace(/(\d{2})\.(\d{3})\.(\d{3})(\d)/, '$1.$2.$3/$4')
            .replace(/(\d{2})\.(\d{3})\.(\d{3})\/(\d{4})(\d{1,2})/, '$1.$2.$3/$4-$5')
            .substring(0, 18);
    };

    // --- MODIFICADO: fetchClients agora usa paginação ---
    const fetchClients = useCallback(async (page = 1, search = '', classification = 'Todos') => {
        setLoading(true);
        setError('');
        try {
            const params = new URLSearchParams({
                search: search,
                classification: classification
            });
            
            const data = await apiFetch(`/general-clients?${params.toString()}`);
            
            // CORREÇÃO CRÍTICA: Lendo o array diretamente, pois a rota de clientes gerais
            // NÃO retorna a estrutura paginada { clients: [...], totalPages: X } neste momento.
            const listData = Array.isArray(data) ? data : (data.clients || []); 
            
            setClients(listData);
            setTotalPages(1); // Força 1 página, já que a API não retorna paginação ainda
            setImgError({});

        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [apiFetch]);

    // --- MODIFICADO: useEffect agora usa debouncedSearchQuery e currentPage ---
    useEffect(() => {
        // A página é sempre 1 por enquanto (sem paginação no backend).
        fetchClients(currentPage, debouncedSearchQuery, filterClassification);
    }, [fetchClients, currentPage, debouncedSearchQuery, filterClassification]); 

    // --- NOVO: useEffect para resetar a página ao pesquisar ou filtrar ---
    useEffect(() => {
        if (currentPage !== 1) {
            setCurrentPage(1);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearchQuery, filterClassification]);

    // Agrupa os clientes por letra inicial
    const groupedClients = useMemo(() => {
        // MODIFICADO: Garante que 'clients' seja um array antes de reduzir
        if (!Array.isArray(clients)) return {};
        
        return clients.reduce((acc, client) => {
            // Garante que 'client.name' não seja nulo e que o client.name[0] exista
            const letter = (client.name && client.name.length > 0) ? client.name[0].toUpperCase() : '?'; 
            if (!acc[letter]) {
                acc[letter] = [];
            }
            acc[letter].push(client);
            return acc;
        }, {});
    }, [clients]);

    // MODIFICADO: A pesquisa agora é reativa (via debouncedSearchQuery)
    const handleSearchSubmit = (e) => {
        e.preventDefault();
        // A busca já é acionada pelo debouncedSearchQuery
    };

    // NOVO: Handler para máscara no campo de pesquisa
    const handleSearchChange = (e) => {
        const value = e.target.value;
        const onlyNums = value.replace(/[^\d]/g, '');
        
        // Se parece um CPF ou CNPJ, aplica a máscara
        if (onlyNums.length > 11 && onlyNums.length <= 14) {
            setSearchMaskedValue(formatCNPJ(value));
        } else if (onlyNums.length > 0 && onlyNums.length <= 11) {
            setSearchMaskedValue(formatCPF(value));
        } else {
            setSearchMaskedValue(value);
        }
        // Sempre define o searchQuery limpo para a API
        setSearchQuery(value.replace(/[^\w]/g, ''));
    };
    
    // --- NOVO: Handler de Paginação ---
    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };

    const openCreateModal = () => {
        setEditingClient(null);
        setIsModalOpen(true);
    };

    const openEditModal = (client) => {
        setEditingClient(client);
        setIsModalOpen(true);
    };
    
    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingClient(null);
        setIsDeleteModalOpen(false); // Garante que o modal de exclusão feche
        setClientToDelete(null);
    };
    
    // --- Handlers de Exclusão ---
    const handleDeleteClient = (client) => {
        setClientToDelete(client);
        setIsDeleteModalOpen(true);
    };
    
    const handleConfirmDelete = async () => {
        if (!clientToDelete) return;
        
        setLoading(true); // Usa o loading principal para cobrir a página
        
        try {
            await apiFetch(`/general-clients/${clientToDelete.id}`, { method: 'DELETE' });
            
            // Recarrega a página atual para remover o item
            fetchClients(currentPage, debouncedSearchQuery, filterClassification); 
            
            // Notifica o App.js para atualizar o dashboard
            onDataChange();
        } catch (err) {
            alert(`Erro ao excluir cliente: ${err.message}`); // Usar alert temporário
            setError(err.message);
            setLoading(false);
        } finally {
            handleCloseModal();
        }
    };
    
    const toggleClientDetails = (id) => {
        setExpandedClientId(prevId => (prevId === id ? null : id));
    };

    return (
        <div>
            <h1 className="text-3xl font-bold text-gray-800">Cadastro de Clientes / Fornecedores</h1>
            <p className="mb-6 text-gray-600">Gira os clientes e fornecedores da sua entidade.</p>

            <div className="p-4 mb-6 bg-white rounded-lg shadow-sm">
                 <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <form onSubmit={handleSearchSubmit} className="md:col-span-2 flex gap-2">
                        <div className="flex-grow">
                            <label htmlFor="search" className="text-sm font-medium">Pesquisar</label>
                            <input 
                                type="text" 
                                id="search"
                                value={searchMaskedValue} 
                                onChange={handleSearchChange} 
                                placeholder="Nome, nº, email, telefone, documento..." 
                                className="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md"
                            />
                        </div>
                        <button type="submit" className="self-end px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700">
                            <Search size={20} />
                        </button>
                    </form>
                    
                    <div>
                        <label htmlFor="classificationFilter" className="text-sm font-medium">Filtrar por Tipo</label>
                        <select 
                            id="classificationFilter" 
                            value={filterClassification} 
                            // MODIFICADO: Atualiza o filtro e reseta a página (via useEffect)
                            onChange={e => setFilterClassification(e.target.value)}
                            className="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md"
                        >
                            <option value="Todos">Todos</option>
                            <option value="Cliente">Apenas Clientes</option>
                            <option value="Fornecedor">Apenas Fornecedores</option>
                            <option value="Ambos">Apenas Ambos</option>
                        </select>
                    </div>

                    <div className="self-end">
                        {canCreate && (
                            <button onClick={openCreateModal} className="flex items-center justify-center w-full gap-2 px-4 py-2 font-semibold text-white bg-green-600 rounded-md hover:bg-green-700">
                                <UserPlus size={20} /> Novo Cliente / Fornecedor
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* MODIFICADO: Spinner de carregamento */}
            {loading && <div className="flex justify-center items-center p-8"><Loader2 className="animate-spin text-blue-600" size={32} /></div>}
            {error && <p className="text-red-600 p-4 bg-red-100 rounded-md">{error}</p>}

            {!loading && !error && (
                <div className="space-y-4">
                    {/* MODIFICADO: Mensagem de 'Nenhum resultado' */}
                    {clients.length === 0 ? (
                        <p className="p-6 text-center bg-white rounded-lg shadow text-gray-500">
                            {searchQuery || filterClassification !== 'Todos' ? 'Nenhum resultado encontrado para os filtros aplicados.' : 'Nenhum cliente ou fornecedor cadastrado.'}
                        </p>
                    ) : (
                        Object.keys(groupedClients).map(letter => (
                            <div key={letter}>
                                <h2 className="text-2xl font-bold text-gray-500 mt-6 mb-2 pb-2 border-b">{letter}</h2>
                                <div className="space-y-4">
                                    {groupedClients[letter].map(client => {
                                        const clientPhotoUrl = client.profile_picture_path ? `/api/uploads/generic_images/${client.profile_picture_path}?t=${new Date().getTime()}` : null;
                                        const clientImgError = imgError[client.id];
                                        const contactInfo = [client.phone, client.email].filter(Boolean).join(' | ') || 'Sem contacto';
                                        
                                        let formattedDocId = client.document_id;
                                        if (client.document_type === 'CPF') formattedDocId = formatCPF(client.document_id);
                                        if (client.document_type === 'CNPJ') formattedDocId = formatCNPJ(client.document_id);
                                        const docInfo = [client.document_type, formattedDocId].filter(Boolean).join(': ') || 'Sem documento';

                                        let classificationBadge;
                                        switch(client.classification) {
                                            case 'Fornecedor':
                                                classificationBadge = <span className="ml-2 text-xs font-semibold text-yellow-800 bg-yellow-100 px-2 py-0.5 rounded-full">{client.classification}</span>;
                                                break;
                                            case 'Ambos':
                                                classificationBadge = <span className="ml-2 text-xs font-semibold text-indigo-800 bg-indigo-100 px-2 py-0.5 rounded-full">{client.classification}</span>;
                                                break;
                                            case 'Cliente':
                                            default:
                                                classificationBadge = <span className="ml-2 text-xs font-semibold text-green-800 bg-green-100 px-2 py-0.5 rounded-full">{client.classification || 'Cliente'}</span>;
                                        }

                                        // Badge Novo: Indicador Visual de Cliente de Repasse na Lista
                                        const repasseBadge = client.is_repasse ? (
                                            <span className="ml-2 text-xs font-bold text-blue-800 bg-blue-100 px-2 py-0.5 rounded-full border border-blue-200">
                                                REPASSE ({client.subclients ? client.subclients.length : 0})
                                            </span>
                                        ) : null;

                                        return (
                                            <div key={client.id} className="bg-white rounded-lg shadow-md">
                                                <div 
                                                    className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50" 
                                                    onClick={() => toggleClientDetails(client.id)}
                                                >
                                                    <div className="flex items-center gap-4">
                                                        <div className="flex-shrink-0">
                                                            {clientPhotoUrl && !clientImgError ? (
                                                                <img src={clientPhotoUrl} alt={client.name} className="w-12 h-12 rounded-full object-cover bg-gray-200" onError={() => setImgError(e => ({ ...e, [client.id]: true }))} />
                                                            ) : (
                                                                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                                                                    <User className="w-6 h-6 text-blue-600" />
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div>
                                                            <p className="font-bold text-gray-800">{client.name} <span className="text-sm font-normal text-gray-500">(#{client.client_number})</span></p>
                                                            <p className="text-sm text-gray-600">{contactInfo}</p>
                                                            <p className="text-sm text-gray-500 flex items-center mt-1">
                                                                {docInfo}
                                                                {classificationBadge}
                                                                {repasseBadge}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-4">
                                                        {canEdit && (
                                                            <button 
                                                                onClick={(e) => { e.stopPropagation(); openEditModal(client); }}
                                                                className="p-2 rounded-full hover:bg-gray-200"
                                                                title="Editar Cadastro"
                                                            >
                                                                <Edit size={18} />
                                                            </button>
                                                        )}
                                                        {canDelete && (
                                                            <button 
                                                                onClick={(e) => { e.stopPropagation(); handleDeleteClient(client); }} 
                                                                className="p-2 text-red-600 rounded-full hover:bg-red-100"
                                                                title="Excluir Cadastro"
                                                            >
                                                                <Trash2 size={18} />
                                                            </button>
                                                        )}
                                                        
                                                        <div className="w-6 h-6 flex items-center justify-center">
                                                            {expandedClientId === client.id ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                                                        </div>
                                                    </div>
                                                </div>
                                                
                                                {expandedClientId === client.id && (
                                                    <div className="p-4 bg-white border-t space-y-3 text-sm text-gray-700">
                                                        <h3 className="font-bold border-b pb-1 mb-2">Detalhes de Contato</h3>
                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-y-2">
                                                            <div className="flex items-center gap-2"><MapPin size={16} /> Endereço: {client.address_street}, {client.address_number} - {client.address_city}</div>
                                                            <div className="flex items-center gap-2"><Mail size={16} /> Email: {client.email || 'N/A'}</div>
                                                            <div className="flex items-center gap-2"><Phone size={16} /> Telefone: {client.phone || 'N/A'}</div>
                                                            <div className="flex items-center gap-2"><User size={16} /> Responsável: {client.responsible_username || 'N/A'}</div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>
                        ))
                    )}
                    
                    {/* --- Controles de Paginação --- */}
                    {/* Mantido o div para alinhamento futuro */}
                    <div className="flex items-center justify-center gap-4 mt-6">
                        {/* Como não há paginação no backend, removemos os botões. */ }
                    </div>
                </div>
            )}

            {isModalOpen && (
                <ClientFormModal
                    clientToEdit={editingClient}
                    onClose={handleCloseModal}
                    onFinish={handleCloseModal}
                    onDataChange={() => {
                        onDataChange(); 
                        // Recarrega a página atual
                        fetchClients(currentPage, debouncedSearchQuery, filterClassification); 
                    }}
                />
            )}
            
            {isDeleteModalOpen && clientToDelete && (
                 <ConfirmDeleteModal
                    onClose={handleCloseModal}
                    onConfirm={handleConfirmDelete}
                    message={`Tem certeza que deseja excluir o cadastro de ${clientToDelete.name}? Esta ação não pode ser desfeita.`}
                    // Adicione loading/error se necessário
                />
            )}
        </div>
    );
};

export default GenericClientsPage;